import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  borderColors,
  ColorsConstants,
  inputColor,
} from "../cssUtils/constants";
import { ReactComponent as Search } from "../assets/images/icons/search/search.svg";
import typography from "../cssUtils/typography";

/**
 * TableAutocomplete — async autocomplete with the same visual style as TableSearchInput.
 *
 * Props:
 *  - value: any — controlled value
 *  - inputValue: string — controlled input text
 *  - onInputChange: (event, value) => void
 *  - onChange: (event, value) => void — called when an option is selected
 *  - options: array — list of options
 *  - loading: boolean
 *  - getOptionLabel: (option) => string
 *  - isOptionEqualToValue: (option, value) => boolean
 *  - renderOption: (props, option) => ReactNode — custom option renderer
 *  - tooltip: string
 *  - placeholder: string
 *  - open: boolean
 *  - onOpen: () => void
 *  - onClose: () => void
 *  - sx: object — extra sx for the Autocomplete root
 *  - freeSolo: boolean — allow free text that isn't one of the options (search box with suggestions)
 *  - width: number | string — input width (default 259)
 */
const TableAutocomplete = ({
  value,
  inputValue,
  onInputChange,
  onChange,
  options = [],
  loading = false,
  getOptionLabel = (option) => option.label ?? String(option),
  isOptionEqualToValue,
  renderOption,
  filterOptions = (x) => x,
  tooltip = "",
  placeholder = "Search",
  open,
  onOpen,
  onClose,
  sx = {},
  freeSolo = false,
  width = 259,
}) => {
  const textFieldSx = {
    borderRadius: "8px",
    width: "100%",
    ...typography.bodyLarge,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px !important",
      height: 40,
      color: inputColor,
      ...typography.bodyLarge,

      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "8px",
        borderColor: borderColors.inputFieldGrey,
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: ColorsConstants.primaryGray50,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: ColorsConstants.primaryGray100,
      },

      "& .MuiOutlinedInput-input": {
        color: inputColor,
        ...typography.bodyLarge,

        "&::placeholder": {
          color: inputColor,
          opacity: 1,
          ...typography.bodyLarge,
        },
      },
    },
  };

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <Autocomplete
        size="small"
        freeSolo={freeSolo}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        value={value}
        inputValue={inputValue}
        onInputChange={onInputChange}
        onChange={onChange}
        options={options}
        loading={loading}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        renderOption={renderOption}
        sx={{ width, ...sx }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            autoComplete="off"
            sx={textFieldSx}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search color={inputColor} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Tooltip>
  );
};

export default TableAutocomplete;